import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    X,
    MapPin,
    Activity,
    Signal,
    Battery,
    Calendar,
    Thermometer,
    Droplets,
    Wind,
    Sun,
    Building,
    Code,
    Settings,
    Clock,
    FileText
} from 'lucide-react';
import { Button, Badge } from '../ui';
import { resolveIntlLocale } from '../../utils/locale';
import './ViewStationModal.css';

export function ViewStationModal({ isOpen, onClose, station }) {
    const { t, i18n } = useTranslation();

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Handle ESC key
    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen || !station) return null;

    const locale = resolveIntlLocale(i18n.resolvedLanguage || i18n.language);

    const getSignalQuality = (dbm) => {
        if (dbm === undefined || dbm === null) return { label: t('stations.signalQuality.unknown'), color: '#6B6B6B' };
        if (dbm > -60) return { label: t('stations.signalQuality.excellent'), color: '#5A7A3A' };
        if (dbm > -75) return { label: t('stations.signalQuality.good'), color: '#7A8A5E' };
        if (dbm > -85) return { label: t('stations.signalQuality.fair'), color: '#8A7A4A' };
        return { label: t('stations.signalQuality.poor'), color: '#8A4A3A' };
    };

    const signalQuality = getSignalQuality(station.signalStrength);

    const formatDate = (dateString) => {
        if (!dateString) return t('common.notAvailable');
        try {
            return new Date(dateString).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (date) => {
        if (!date) return t('common.notAvailable');
        try {
            return new Date(date).toLocaleString(locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return t('common.notAvailable');
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalContent = (
        <div className="modal-overlay" onClick={handleBackdropClick}>
            <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">{t('stations.viewModal.title')}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '24px' }}>
                    {/* Header Section */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start', 
                        marginBottom: '24px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid #E5E7EB'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <Code size={20} color="#4A5A3A" />
                                <h3 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{station.stationCode}</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                                <Badge variant={station.status === 'active' ? 'success' : station.status === 'warning' ? 'warning' : 'danger'}>
                                    {t(`stations.status.${station.status}`)}
                                </Badge>
                                <Badge variant="secondary" style={{ 
                                    background: station.stationType === 'campo_aperto' 
                                        ? 'rgba(90, 122, 58, 0.12)' 
                                        : 'rgba(74, 90, 58, 0.12)',
                                    color: station.stationType === 'campo_aperto' ? '#5A7A3A' : '#4A5A3A'
                                }}>
                                    {station.stationType === 'campo_aperto'
                                        ? t('stations.types.campo_aperto')
                                        : t('stations.types.retro_modulo')}
                                </Badge>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6B7280', fontSize: '14px', marginBottom: '4px' }}>
                                <Building size={16} />
                                {station.farmName}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '13px' }}>
                                <MapPin size={14} />
                                {station.locationDescription || t('common.notAvailable')}
                            </div>
                        </div>
                    </div>

                    {/* Basic Information Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            marginBottom: '16px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Settings size={18} />
                            {t('stations.form.sections.basicInformation')}
                        </h4>
                        <div style={{ 
                            background: '#F9FAFB', 
                            borderRadius: '8px', 
                            padding: '16px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px'
                        }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.code')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>{station.stationCode}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.type')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                                    {station.stationType === 'campo_aperto'
                                        ? t('stations.types.campo_aperto')
                                        : t('stations.types.retro_modulo')}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.farm')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>{station.farmName}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.table.status')}</div>
                                <div>
                                    <Badge variant={station.status === 'active' ? 'success' : station.status === 'warning' ? 'warning' : 'danger'}>
                                        {t(`stations.status.${station.status}`)}
                                    </Badge>
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.locationDescription')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>
                                    {station.locationDescription || t('common.notAvailable')}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.viewModal.activeStatusLabel')}</div>
                                <div>
                                    <Badge variant={station.isActive !== false ? 'success' : 'danger'}>
                                        {station.isActive !== false ? t('users.table.status.active') : t('users.table.status.inactive')}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Coordinates Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            marginBottom: '16px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <MapPin size={18} />
                            {t('stations.form.sections.locationCoordinates')}
                        </h4>
                        <div style={{ 
                            background: '#F9FAFB', 
                            borderRadius: '8px', 
                            padding: '16px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px'
                        }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.latitude')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', fontFamily: 'monospace' }}>
                                    {station.locationLat !== undefined ? station.locationLat.toFixed(6) : t('common.notAvailable')}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.longitude')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', fontFamily: 'monospace' }}>
                                    {station.locationLon !== undefined ? station.locationLon.toFixed(6) : t('common.notAvailable')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Configuration Section */}
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            marginBottom: '16px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Settings size={18} />
                            {t('stations.form.sections.configuration')}
                        </h4>
                        <div style={{ 
                            background: '#F9FAFB', 
                            borderRadius: '8px', 
                            padding: '16px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px'
                        }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.transmissionInterval')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                                    {station.transmissionInterval || 15} {t('time.minutes')}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.form.installDate')}</div>
                                <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>
                                    {formatDate(station.installDate)}
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: '16px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FileText size={14} />
                                {t('stations.form.notes')}
                            </div>
                            <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
                                {station.notes || t('common.notAvailable')}
                            </p>
                        </div>
                    </div>

                    {/* Current Readings Section */}
                    {station.currentReadings && (
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                marginBottom: '16px', 
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <Activity size={18} />
                                {t('stations.viewModal.currentReadings')}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FFFFFF' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '8px', 
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Thermometer size={20} color="#EF4444" />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>{t('analytics.metrics.temperature')}</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                                                {station.currentReadings.temperature}°C
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FFFFFF' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '8px', 
                                            background: 'rgba(59, 130, 246, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Droplets size={20} color="#3B82F6" />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>{t('analytics.metrics.humidity')}</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                                                {station.currentReadings.humidity}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FFFFFF' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '8px', 
                                            background: 'rgba(107, 114, 128, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Wind size={20} color="#6B7280" />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>{t('analytics.metrics.windSpeed')}</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                                                {station.currentReadings.windSpeed} m/s
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FFFFFF' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '8px', 
                                            background: 'rgba(245, 158, 11, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Sun size={20} color="#F59E0B" />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>{t('analytics.metrics.radiation')}</div>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                                                {station.currentReadings.solarRadiation} W/m²
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {station.currentReadings.timestamp && (
                                <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280', textAlign: 'right' }}>
                                    {t('stations.viewModal.lastReading', { time: formatDateTime(station.currentReadings.timestamp) })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* System Information Section */}
                    <div style={{ 
                        borderTop: '1px solid #E5E7EB', 
                        paddingTop: '24px'
                    }}>
                        <h4 style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            marginBottom: '16px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Activity size={18} />
                            {t('stations.viewModal.systemInformation')}
                        </h4>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(3, 1fr)', 
                            gap: '16px'
                        }}>
                            <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.viewModal.signalStrength')}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: signalQuality.color }}>
                                    <Signal size={18} />
                                    <span>{signalQuality.label}</span>
                                </div>
                                {station.signalStrength && (
                                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                                        {station.signalStrength} dBm
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.viewModal.batteryLevel')}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                                    <Battery size={18} />
                                    <span>{station.batteryLevel !== undefined ? `${station.batteryLevel}%` : t('common.notAvailable')}</span>
                                </div>
                            </div>
                            <div style={{ padding: '12px', background: '#F9FAFB', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('stations.viewModal.lastTransmission')}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                                    <Clock size={18} />
                                    <span style={{ fontSize: '13px' }}>
                                        {station.lastTransmission ? formatDateTime(station.lastTransmission) : t('stations.never')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
                    <Button variant="secondary" onClick={onClose}>
                        {t('common.close')}
                    </Button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}
